import { subYears } from 'date-fns';
import React, { useState } from 'react';
import { Dimensions, Pressable, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import type { CustomerDocumentAsset } from '@/api/partners/organizations/types';
import { useCustomerDocuments } from '@/api/partners/organizations/use-customer-documents';
import { usePaginatedCustomers } from '@/api/partners/organizations/use-paginated-customers';
import { useDebounce } from '@/core/hooks/use-debounced-value';
import { useHasOrganization } from '@/core/hooks/use-has-organization';
import { toDateOnlyString } from '@/core/utils';
import { Button, Card, Input, Text } from '@/ui';
import { Calendar } from '@/ui/calendar';
import { Icon } from '@/ui/icon';
import { Search } from '@/ui/icons';
import { Persons } from '@/ui/icons/persons';
import { Loader } from '@/ui/loader';

import { AddCustomerModal } from './add-customer-modal';
import { CustomerDocumentsModal } from './customer-documents-modal';
import type { DocumentCategory } from './customers-table';
import { CustomersTable } from './customers-table';
import { ImportCustomerModal } from './import-customer-modal';
import { CustomerStatusLegend } from './status-icon';

const PAGE_HEIGHT = Dimensions.get('window').height;
const ROWS_PER_PAGE = 20;

export default function Customers() {
  const { styles, theme } = useStyles(stylesheet);
  const { hasOrganization } = useHasOrganization();

  const [text, setText] = useState('');
  const debouncedText = useDebounce(text, 400);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [page, setPage] = useState(0);
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [showImportCustomers, setShowImportCustomers] = useState(false);
  const [docTarget, setDocTarget] = useState<{
    customerId: number;
    category: DocumentCategory;
  } | null>(null);

  const { data: documents, isFetching: isFetchingDocuments } =
    useCustomerDocuments({
      variables: { customerId: docTarget?.customerId! },
      enabled: !!docTarget,
    });

  const documentAssets: CustomerDocumentAsset[] = React.useMemo(() => {
    if (!docTarget || !documents) {
      return [];
    }

    if (docTarget.category === 'license') {
      const license = documents.driversLicense;
      if (!license) {
        return [];
      }
      return [license.front, license.back, license.selfie].filter(
        (asset): asset is CustomerDocumentAsset => !!asset
      );
    }

    return documents.insuranceDocuments ?? [];
  }, [docTarget, documents]);

  const { data, isPending, isSuccess, isFetchingNextPage, fetchNextPage } =
    usePaginatedCustomers({
      enabled: hasOrganization,
      text: debouncedText.trim() || undefined,
      startDate: toDateOnlyString(startDate),
      endDate: toDateOnlyString(endDate),
      perPage: ROWS_PER_PAGE,
    });

  const totalCount = data?.pages[0]?.meta.totalCount ?? 0;

  const pagedCustomers = data?.pages[page]?.data ?? [];

  const hasActiveFilters =
    debouncedText.trim().length > 0 || !!startDate || !!endDate;
  const hasCustomers = totalCount > 0;
  const hasDateRange = !!startDate && !!endDate;

  const handleAddCustomer = () => {
    setShowAddCustomer(true);
  };

  const handleImportCustomers = () => {
    setShowImportCustomers(true);
  };

  if (isPending && !data) {
    return (
      <View style={styles.fullPageLoader}>
        <Loader style={{ height: 250, width: 250 }} />
      </View>
    );
  }

  if (isSuccess && !hasCustomers && !hasActiveFilters) {
    return (
      <View style={styles.emptyWrapper}>
        <View style={styles.emptyState}>
          <View style={styles.emptyIconCircle}>
            <Icon
              type="naked"
              icon={<Persons />}
              fillColor={theme.colors.icon.color}
              width={24}
              height={16}
            />
          </View>

          <View style={{ alignItems: 'center', gap: 8 }}>
            <Text type="title2">No Customers Yet</Text>

            <Text
              type="bodyRegular"
              style={{
                color: theme.colors.text.secondary,
                textAlign: 'center',
              }}
            >
              They'll appear here automatically after their first booking. Or
              add them yourself to get a head start.
            </Text>
          </View>

          <View style={styles.emptyButtons}>
            <Button
              text="Add Customers"
              type="primarySmall"
              onPress={handleAddCustomer}
              style={{ width: 'auto' }}
            />

            <Button
              text="Import Customers"
              type="secondarySmall"
              onPress={handleImportCustomers}
              style={{ width: 'auto' }}
            />
          </View>
        </View>

        <AddCustomerModal
          isVisible={showAddCustomer}
          onClose={() => setShowAddCustomer(false)}
        />

        <ImportCustomerModal
          isVisible={showImportCustomers}
          onClose={() => setShowImportCustomers(false)}
        />
      </View>
    );
  }

  return (
    <View style={styles.page}>
      <View style={styles.container}>
        <View style={styles.header}>
          <Text type="title1">Customers</Text>

          <View style={styles.headerButtons}>
            <Button
              text="Import Customers"
              type="secondarySmall"
              onPress={handleImportCustomers}
              style={{ width: 'auto' }}
            />

            <Button
              text="Add Customer"
              type="primarySmall"
              onPress={handleAddCustomer}
              style={{ width: 'auto' }}
            />
          </View>
        </View>

        <View style={styles.toolbar}>
          <View style={styles.searchInput}>
            <Input
              label=""
              placeholder="Search for customers..."
              value={text}
              onChangeText={(value: string) => {
                setText(value);
                setPage(0);
              }}
              leftIcon={
                <Icon
                  type="naked"
                  icon={<Search />}
                  fillColor={theme.colors.icon.color}
                  width={22}
                  height={22}
                />
              }
            />
          </View>

          <CustomerStatusLegend />
        </View>

        <View style={styles.filterRow}>
          <Card
            style={{
              paddingVertical: 8,
              paddingHorizontal: 16,
              borderRadius: 8,
              borderColor: hasDateRange
                ? theme.colors.buttonFilter.active.border
                : 'transparent',
            }}
          >
            <Pressable onPress={() => setShowCalendar(true)}>
              <Text type="bodyRegular">Date range</Text>
            </Pressable>
          </Card>

          <View style={{ zIndex: 1 }}>
            <Calendar
              mode="date"
              isVisible={showCalendar}
              headerTitle="Date"
              minDate={subYears(new Date(), 5)}
              defaultStartDate={startDate}
              defaultEndDate={endDate}
              onHide={() => setShowCalendar(false)}
              onClear={() => {
                setStartDate(null);
                setEndDate(null);
                setPage(0);
                setShowCalendar(false);
              }}
              onDatesSelected={(start, end) => {
                setStartDate(start);
                setEndDate(end);
                setPage(0);
                setShowCalendar(false);
              }}
              onDateSelected={() => {}}
              onTimeSelected={() => {}}
              range
              style={{
                zIndex: 1,
                position: 'absolute',
                top: -75,
                left: 100,
              }}
              containerStyle={{
                borderWidth: 1,
                borderColor: theme.colors.card.default.borderColor,
                shadowColor: theme.colors.card.default.borderColor,
                shadowOffset: { width: 0, height: 5 },
                shadowOpacity: 0.5,
                shadowRadius: 5,
                elevation: 10,
                height: '100%',
              }}
              id="customers-calendar"
              hasBackdrop
              coverScreen
              showDesktopOverview
            />
          </View>
        </View>

        <View style={styles.tableContainer}>
          {isPending && !isFetchingNextPage ? (
            <View style={styles.tableLoader}>
              <Loader style={{ height: 250, width: 250 }} />
            </View>
          ) : !hasCustomers && hasActiveFilters ? (
            <View style={styles.noResults}>
              <Text type="title2">No customers found</Text>

              <Text
                type="bodyRegular"
                style={{ color: theme.colors.text.secondary }}
              >
                Try adjusting your search or date range.
              </Text>
            </View>
          ) : (
            <CustomersTable
              customers={pagedCustomers}
              page={page}
              total={totalCount}
              rowsPerPage={ROWS_PER_PAGE}
              onPageChange={(p: number) => {
                const currentPage = data?.pages.at(-1)?.meta.page ?? 1;

                if (p > currentPage - 1) {
                  fetchNextPage();
                }

                setPage(p);
              }}
              onDocumentsPress={(customerId, category) =>
                setDocTarget({ customerId, category })
              }
            />
          )}
        </View>
      </View>

      <AddCustomerModal
        isVisible={showAddCustomer}
        onClose={() => setShowAddCustomer(false)}
      />

      <ImportCustomerModal
        isVisible={showImportCustomers}
        onClose={() => setShowImportCustomers(false)}
      />

      <CustomerDocumentsModal
        isVisible={!!docTarget}
        assets={documentAssets}
        isLoading={isFetchingDocuments}
        onClose={() => setDocTarget(null)}
      />
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  page: {
    flex: 1,
    backgroundColor: theme.colors.background_1,
    maxHeight: PAGE_HEIGHT,
  },
  container: {
    flex: 1,
    paddingTop: { xs: 24, md: 56 },
    paddingLeft: { xs: 16, md: 56 },
    paddingRight: { xs: 16, md: 56 },
    paddingBottom: { xs: 16, md: 56 },
    gap: 24,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
  },
  headerButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  toolbar: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
    flexWrap: 'wrap',
  },
  searchInput: {
    width: { xs: '100%', md: 320 },
  },
  filterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    zIndex: 1,
  },
  tableContainer: {
    flex: {
      xs: 1,
      md: undefined,
    },
  },
  fullPageLoader: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.background_1,
  },
  tableLoader: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  noResults: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  emptyWrapper: {
    flex: 1,
    backgroundColor: theme.colors.background_1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyState: {
    alignItems: 'center',
    gap: 16,
    padding: 16,
    maxWidth: 640,
  },
  emptyIconCircle: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: theme.colors.card.default.backgroundColor,
    borderWidth: 1,
    borderColor: theme.colors.card.default.borderColor,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
}));
